import { useParams } from "react-router-dom";
import Layout from "../layout.tsx/app";
import { useEffect, useState } from "react";
import LoadingPage from "../layout.tsx/loading";
import { Button } from "../../components/buttons";
import BaseModal from "../../components/modal/base";
import { FormProvider, useForm } from "react-hook-form";
import { FormTextArea } from "../../components/forms/input-textarea";
import { FileInput, Label, Spinner } from "flowbite-react";
import { useReport } from "../../stores/report";
import { request } from "../../api/config";
import { Check, Clock, FilePdf, WarningCircle } from "@phosphor-icons/react";
import { useAlert } from "../../stores/alert";
import { parseDate } from "../../helper/date";
import { ReportType } from "../../types/report";

type FormValues = {
  file: FileList;
  notes: string;
};

type ErrorForms = {
  file: [] | null;
  notes: [] | null;
};

const ReportActivationCode = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [modalAdd, setModalAdd] = useState<boolean>(false);
  const [revised, setRevised] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorForms | null>(null);

  const forms = useForm<FormValues>();

  const { code } = useParams();
  const { reports, setReports } = useReport();
  const { setMessage } = useAlert();

  const handleUpload = forms.handleSubmit(async (data) => {
    setLoadingSubmit(true);
    try {
      let payload = {
        file: data.file[0],
        activation_code: code,
        notes: data.notes,
        revised: revised ?? null,
      };
      await request
        .post("/report", payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          setMessage("Berhasil mengunggah laporan", "success");
          setModalAdd(false);
          setRevised(null);
          forms.reset();
        });
    } catch (err: any) {
      setErrors(err.response.data.errors);
      setMessage("Oops, ada yang error nih", "error");
    }
    setLoadingSubmit(false);
  });

  const getReport = async () => {
    setLoading(true);
    try {
      const { data } = await request.get("/report", {
        params: {
          activation_code: code,
        },
      });
      return data.data;
    } catch (err: any) {
      console.log(err);
    }
  };

  useEffect(() => {
    Promise.all([getReport()]).then((res) => {
      setReports(res[0]);
      setLoading(false);
    });
  }, [loadingSubmit]);

  return (
    <Layout
      withPageTitle
      title={`Laporan Tes #${code}`}
      pageTitleContent={<Status items={reports?.data ?? null} />}
    >
      <>
        {loading ? (
          <LoadingPage />
        ) : (
          <>
            {reports?.data.length === 0 ? (
              <div className="mt-4 w-full h-[150px] flex items-center justify-center">
                <div className="text-center">
                  <span className="text-xl">Belum ada laporan</span> <br />
                  <div className="mt-3">
                    <Button className="px-12" onClick={() => setModalAdd(true)}>
                      Buat Laporan
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {reports?.data.map((item, key) => (
                  <div className="w-full rounded-lg mb-2 p-3 border" key={key}>
                    <div className="w-full flex justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-9">
                          <FilePdf size={32} />
                        </div>
                        <div className="leading-5">
                          <a
                            href={item.file ?? "-"}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline hover:text-blue-700"
                          >
                            {item.filename ?? "-"}
                          </a>{" "}
                          <br />
                          <small>
                            Tgl Upload:{" "}
                            {item.created_at ? parseDate(item.created_at) : "-"}
                          </small>
                        </div>
                      </div>
                      <div>
                        {!item.revised_at && !item.approved_at && (
                          <div className="flex items-center gap-2 text-yellow-500">
                            <Clock />
                            <span className="text-sm">Belum Diperiksa</span>
                          </div>
                        )}
                        {item.revised_at && !item.has_revised_at && (
                          <div className="flex items-center gap-2 text-red-700">
                            <WarningCircle />
                            <span className="text-sm">Perlu direvisi!</span>
                          </div>
                        )}

                        {item.has_revised_at && (
                          <div className="flex items-center gap-2 text-blue-700">
                            <WarningCircle />
                            <span className="text-sm">Sudah Direvisi!</span>
                          </div>
                        )}

                        {item.approved_at && (
                          <div className="flex items-center gap-2 text-green-700">
                            <Check />
                            <span className="text-sm">Diterima</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {item.approved_file && (
                      <div className="mt-4 bg-green-50 py-2 px-4 rounded-lg">
                        <small className="text-green-700">
                          File laporan yang sudah disetujui
                        </small>
                        <div className="flex items-center gap-2">
                          <div className="w-9">
                            <FilePdf size={32} />
                          </div>
                          <div className="leading-5">
                            <a
                              href={item.approved_file ?? "-"}
                              target="_blank"
                              rel="noreferrer"
                              className="hover:underline hover:text-blue-700"
                            >
                              {item.approved_filename ?? "-"}
                            </a>{" "}
                            <br />
                            <small>
                              Tgl Upload:{" "}
                              {item.updated_at
                                ? parseDate(item.updated_at)
                                : "-"}
                            </small>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 ">
                      <small>Catatan Psikolog:</small> <br />
                      <span>{item.notes}</span>
                    </div>

                    {item.revise_note && (
                      <div className="mt-3 p-3 border-t bg-red-50">
                        <small>Catatan Revisi:</small> <br />
                        <span>{item.revise_note}</span>
                      </div>
                    )}

                    {item.revised_at && !item.has_revised_at && (
                      <div className="mt-3 flex items-center py-2 justify-end">
                        <Button
                          className="text-sm px-12"
                          onClick={() => {
                            setModalAdd(true);
                            setRevised(item.id);
                          }}
                        >
                          Upload file revisi
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </>

      <BaseModal
        title={revised ? "Upload Revisi" : "Buat Laporan"}
        isOpen={modalAdd}
        close={() => {
          setRevised(null);
          setModalAdd(false);
        }}
        size="xl"
      >
        <FormProvider {...forms}>
          <div id="fileUpload" className="mb-4">
            <div className="mb-2 block">
              <Label htmlFor="file" value="Upload Dokumen" />
            </div>
            <FileInput
              {...forms.register("file")}
              id="file"
              helperText="Format dokumen yang didukung hanya PDF atau .docx"
              className="w-full"
              accept=".pdf, .doc, .docx"
            />
            {errors?.file && (
              <small className="text-red-600">
                <>{errors.file}</>
              </small>
            )}
          </div>
          <FormTextArea
            label="Catatan"
            control={forms.control}
            name="notes"
            error={errors?.notes}
          />

          <div className="mt-3 flex items-center justify-end">
            <Button className="px-12" onClick={handleUpload}>
              {loadingSubmit ? <Spinner /> : "Simpan"}
            </Button>
          </div>
        </FormProvider>
      </BaseModal>
    </Layout>
  );
};

const Status = ({ items }: { items: ReportType[] | null }) => {
  let lastItem = null;
  let statusText = "Belum ada laporan";

  if (items) {
    lastItem = items[0];
  }

  if (!lastItem?.approved_at && !lastItem?.revised_at) {
    statusText = "Belum Diperiksa";
  }

  if (lastItem?.revised_at) {
    statusText = "Perlu direvisi";
  }

  if (lastItem?.approved_at) {
    statusText = "Diterima";
  }

  return (
    <>
      {items && (
        <div
          className={`text-sm py-1 px-3  text-white rounded-lg 
            ${statusText === "Belum ada laporan" && "bg-gray-500"}
            ${statusText === "Belum Diperiksa" && "bg-gray-500"}
            ${statusText === "Perlu direvisi" && "bg-red-600"}
            ${statusText === "Diterima" && "bg-green-600"}
          `}
        >
          {statusText}
        </div>
      )}
    </>
  );
};

export default ReportActivationCode;
